import {
  LabHasilItem,
  LabOrderItem,
  isControl,
  normalize,
  waktuOrder
} from "@/utils/labPraInduksi";

export interface RadiologiHasilItem {
  kode?: string;
  nama?: string;
  acc_number?: string;
  description?: string;
  reportDate?: string;
  dokter?: string;
}

export interface RadiologiOrderItem {
  no_transaksi?: string;
  tgl?: string;
  hasil?: Array<RadiologiHasilItem>;
}

export interface SumberLabPenunjang {
  lis_sampel: string;
  acc_date: string;
  tgl_order: string;
}

export interface SumberRontgen {
  no_transaksi: string;
  tgl: string;
  nama: string;
  reportDate: string;
}

export interface PenunjangPraAnestesiHasil {
  values: Record<string, string>;
  labels: Array<string>;
  sumberLab: SumberLabPenunjang | null;
  jumlahOrderLab: number;
  sumberRontgen: SumberRontgen | null;
}

const LABEL: Record<string, string> = {
  hb_ht: "Hb/Ht",
  natrium: "Natrium",
  clorida: "Clorida",
  kalium: "Kalium",
  pt_aptt: "PT/APTT",
  ureum: "Ureum",
  creatinin: "Creatinin",
  hbsag: "HbsAg",
  rontgen_thorax: "Rontgen Thorax"
};

const URUTAN_FIELD = [
  "hb_ht",
  "natrium",
  "clorida",
  "kalium",
  "pt_aptt",
  "ureum",
  "creatinin",
  "hbsag",
  "rontgen_thorax"
];

const MAX_NILAI_LAB = 40;
const MAX_BACAAN_RONTGEN = 250;

const RE_THORAX = /\bthorax\b|\bthoraks\b|\bthorak\b|\btoraks\b|\btorak\b|\bchest\b|\bcxr\b/;

function matchAnalit(nama: string): string | null {
  if (/\bhbsag\b|\bhbs ag\b|hbs antigen/.test(nama) && !/anti/.test(nama)) {
    return "hbsag";
  }
  if (/\baptt\b|thromboplastin/.test(nama) && !isControl(nama)) return "aptt";
  if (
    /\bppt\b|\bpt\b|protrombin|prothrombin/.test(nama) &&
    !isControl(nama)
  ) {
    return "pt";
  }
  if (
    /hemoglobin|\bhgb\b|\bhb\b/.test(nama) &&
    !/hba1c|hbsag|anti hbs|\bhbs\b/.test(nama)
  ) {
    return "hb";
  }
  if (
    /hematokrit|hematocrit|haematokrit|\bhct\b|\bht\b/.test(nama) &&
    !/anti|hbsag/.test(nama)
  ) {
    return "ht";
  }
  if (/natrium|sodium|\bna\b/.test(nama) && !/urin/.test(nama)) {
    return "natrium";
  }
  if (
    /kalium|potassium|\bk\b/.test(nama) &&
    !/vitamin|\bhbs\b|urin/.test(nama)
  ) {
    return "kalium";
  }
  if (
    /klorida|clorida|chlorida|chloride|\bcl\b/.test(nama) &&
    !/urin/.test(nama)
  ) {
    return "clorida";
  }
  if (/\bureum\b|\burea\b/.test(nama) && !/urin|clearance|klirens/.test(nama)) {
    return "ureum";
  }
  if (/\bbun\b/.test(nama)) return "bun";
  if (
    /kreatinin|creatinin|creatinine/.test(nama) &&
    !/klirens|clearance|urin|kinase|\bck\b|egfr/.test(nama)
  ) {
    return "creatinin";
  }

  return null;
}

function nilaiLab(item: LabHasilItem): string {
  const raw = String(item.hasil == null ? "" : item.hasil).trim();

  if (!raw || raw.length > MAX_NILAI_LAB) return "";

  return raw;
}

function ambilAnalitDariOrder(order: LabOrderItem): Record<string, string> {
  const hasil = Array.isArray(order.hasil) ? order.hasil : [];
  const found: Record<string, string> = {};

  hasil.forEach(item => {
    const nama = normalize(item.nama_test);
    if (!nama) return;

    const key = matchAnalit(nama);
    if (!key || found[key] !== undefined) return;

    const nilai = nilaiLab(item);
    if (!nilai) return;

    found[key] = nilai;
  });

  return found;
}

function gabungPasangan(
  kiri: string | undefined,
  kanan: string | undefined,
  labelKiri: string,
  labelKanan: string
): string {
  if (kiri && kanan) return `${kiri} / ${kanan}`;
  if (kiri) return `${labelKiri} ${kiri}`;
  if (kanan) return `${labelKanan} ${kanan}`;

  return "";
}

function waktuRontgen(reportDate?: string, tgl?: string): number {
  const waktu = reportDate || tgl || "";
  const stamp = new Date(String(waktu).replace(" ", "T")).getTime();

  return isNaN(stamp) ? 0 : stamp;
}

function rapikanBacaan(teks: string): string {
  const satu = String(teks)
    .replace(/\s*\r?\n\s*/g, "; ")
    .replace(/\s+/g, " ")
    .trim();

  if (satu.length <= MAX_BACAAN_RONTGEN) return satu;

  return `${satu.slice(0, MAX_BACAAN_RONTGEN).trim()}…`;
}

function ambilRontgenThorax(
  orders: Array<RadiologiOrderItem>
): { nilai: string; sumber: SumberRontgen } | null {
  const kandidat: Array<{
    nilai: string;
    sumber: SumberRontgen;
    stamp: number;
  }> = [];

  (orders || []).forEach(order => {
    const hasil = Array.isArray(order.hasil) ? order.hasil : [];

    hasil.forEach(item => {
      const nama = normalize(item.nama);
      if (!nama || !RE_THORAX.test(nama)) return;

      const bacaan = rapikanBacaan(String(item.description || ""));
      if (!bacaan) return;

      kandidat.push({
        nilai: bacaan,
        sumber: {
          no_transaksi: String(order.no_transaksi || ""),
          tgl: String(order.tgl || ""),
          nama: String(item.nama || "").trim(),
          reportDate: String(item.reportDate || "")
        },
        stamp: waktuRontgen(item.reportDate, order.tgl)
      });
    });
  });

  if (!kandidat.length) return null;

  kandidat.sort((a, b) => b.stamp - a.stamp);

  return { nilai: kandidat[0].nilai, sumber: kandidat[0].sumber };
}

export function mapPenunjangPraAnestesi(
  labOrders: Array<LabOrderItem>,
  radiologiOrders: Array<RadiologiOrderItem>
): PenunjangPraAnestesiHasil {
  const analit: Record<string, string> = {};
  let sumberLab: SumberLabPenunjang | null = null;
  let jumlahOrderLab = 0;

  const terbaruDulu = (labOrders || [])
    .slice()
    .sort((a, b) => waktuOrder(b) - waktuOrder(a));

  terbaruDulu.forEach(order => {
    const found = ambilAnalitDariOrder(order);
    let dipakai = false;

    Object.keys(found).forEach(key => {
      if (analit[key] !== undefined) return;
      analit[key] = found[key];
      dipakai = true;
    });

    if (!dipakai) return;

    jumlahOrderLab += 1;

    if (!sumberLab) {
      sumberLab = {
        lis_sampel: String(order.lis_sampel || ""),
        acc_date: String(order.acc_date || ""),
        tgl_order: String(order.tgl_order || "")
      };
    }
  });

  const values: Record<string, string> = {};

  const hbHt = gabungPasangan(analit.hb, analit.ht, "Hb", "Ht");
  if (hbHt) values.hb_ht = hbHt;

  const ptAptt = gabungPasangan(analit.pt, analit.aptt, "PT", "APTT");
  if (ptAptt) values.pt_aptt = ptAptt;

  if (analit.natrium) values.natrium = analit.natrium;
  if (analit.clorida) values.clorida = analit.clorida;
  if (analit.kalium) values.kalium = analit.kalium;
  if (analit.creatinin) values.creatinin = analit.creatinin;
  if (analit.hbsag) values.hbsag = analit.hbsag;

  if (analit.ureum) {
    values.ureum = analit.ureum;
  } else if (analit.bun) {
    values.ureum = `BUN ${analit.bun}`;
  }

  const rontgen = ambilRontgenThorax(radiologiOrders);
  if (rontgen) values.rontgen_thorax = rontgen.nilai;

  const labels = URUTAN_FIELD.filter(field => values[field] !== undefined).map(
    field => LABEL[field]
  );

  return {
    values,
    labels,
    sumberLab: sumberLab,
    jumlahOrderLab,
    sumberRontgen: rontgen ? rontgen.sumber : null
  };
}

export default { mapPenunjangPraAnestesi };
